/**
 * Build-time + runtime configuration for CloudWatch RUM. Values are injected
 * into `externalConfig.ts` by the publish workflow (same mechanism as the
 * Cognito IDs). If `RUM_APP_MONITOR_ID` is empty (e.g. local dev or a build
 * that didn't inject it), RUM is reported disabled and the whole wrapper
 * degrades to a no-op — the portal must never depend on RUM being up.
 */
import {
  AWS_REGION,
  BASE_URL,
  RUM_APP_MONITOR_ID,
  RUM_MONITOR_BY_REGION,
  SESSION_REPLAY_ENABLED,
} from '../../externalConfig';
import { getRegionalApiBaseUrl } from '../../services/regionalApiStorage';
import { isSessionReplayAllowed } from './replayPolicy';

// Injected by webpack DefinePlugin (see webpack.config.js). Guarded so a
// missing define never throws. (Name is fixed by the DefinePlugin token.)
// eslint-disable-next-line @typescript-eslint/naming-convention
declare const __PORTAL_VERSION__: string;

export interface RumRuntimeConfig {
  enabled: boolean;
  applicationId: string;
  applicationVersion: string;
  /** RUM monitor region (where the app monitor lives). */
  region: string;
  endpoint: string;
  deploymentEnvironment: string;
  releaseChannel: string;
  sessionSampleRate: number;
  /** Whether session replay may be enabled (policy-gated; false in prod). */
  sessionReplayEnabled: boolean;
}

export function getApplicationVersion(): string {
  return typeof __PORTAL_VERSION__ !== 'undefined' ? __PORTAL_VERSION__ : 'unknown';
}

/** Low-cardinality environment label derived from the host (no PII). */
export function getDeploymentEnvironment(): string {
  const host = typeof window !== 'undefined' ? window.location.host : '';
  if (host.includes('localhost')) {
    return 'local';
  }
  if (host.includes('mercury')) {
    return 'mercury';
  }
  if (host.includes('venus') || host.includes('mars')) {
    return 'inner';
  }
  return 'production';
}

export function regionToShortCode(region: string): string {
  const map: Record<string, string> = {
    'eu-central-1': 'euc1',
    'us-west-2': 'usw2',
    'us-east-1': 'use1',
    'ap-northeast-1': 'apne1',
    'ap-southeast-1': 'apse1',
  };
  return map[region] ?? region.replace(/[^a-z0-9]/gi, '');
}

/**
 * Which regional backend the portal is talking to, as a low-cardinality code
 * (euc1 / usw2 / use1 / …). Honors the Auth-Router regional override, falling
 * back to the default BASE_URL, then to the monitor region. PHI-free.
 */
export function getBackendRegionCode(): string {
  const baseUrl = getRegionalApiBaseUrl() || BASE_URL;
  const token = baseUrl.match(/\b(euc1|usw2|use1|apne1|apse1)\b/i);
  if (token && token[1]) {
    return token[1].toLowerCase();
  }
  return regionToShortCode(AWS_REGION);
}

export function getRumConfig(): RumRuntimeConfig {
  const applicationId = (RUM_APP_MONITOR_ID || '').trim();
  const region = AWS_REGION;
  const deploymentEnvironment = getDeploymentEnvironment();
  // Sample everything outside production; sample production at 100% for now
  // (errors are rare and high-value). Tune later if volume/cost requires.
  const sessionSampleRate = 1;
  return {
    enabled: applicationId.length > 0,
    applicationId,
    applicationVersion: getApplicationVersion(),
    region,
    endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
    deploymentEnvironment,
    releaseChannel: 'stable',
    sessionSampleRate,
    sessionReplayEnabled: isSessionReplayAllowed(
      deploymentEnvironment,
      SESSION_REPLAY_ENABLED,
    ),
  };
}

/**
 * RUM config for a clinician whose home region is `homeRegion` (as returned by
 * the Auth Router). If a monitor is mapped for that region in
 * RUM_MONITOR_BY_REGION, returns a config pointed at THAT monitor — its id, its
 * own AWS region, and the matching regional data-plane endpoint — so the
 * region's identity-pool credentials (which are only permitted on that monitor)
 * are authorized. On a map miss (no regional monitor, e.g. eu-central-1 itself,
 * an unknown region, or single-region mercury) it returns the default config
 * unchanged, so those clinicians stay on the default monitor. PHI-free.
 */
export function getRumConfigForRegion(homeRegion: string): RumRuntimeConfig {
  const base = getRumConfig();
  const regionalId = (RUM_MONITOR_BY_REGION[homeRegion] || '').trim();
  if (!regionalId) {
    return base;
  }
  return {
    ...base,
    enabled: true,
    applicationId: regionalId,
    region: homeRegion,
    endpoint: `https://dataplane.rum.${homeRegion}.amazonaws.com`,
  };
}
